<template>
  <div id="home">
    <div class="app-background"></div>

    <div v-if="!isStoreInitialized" class="initialization-overlay">
      <div class="loading-spinner"></div>
      <p>Verifying session...</p>
    </div>

    <div v-else class="app-layout">
      <Sidebar 
        :isSearching="isLoading" 
        :searchMode="searchMode"
        @update-filters="handleFilterUpdate"
        @set-mode="setSearchMode"
      />
      <div class="main-content" ref="container">


      <!-- Steam Explanation (Always visible in steam mode) -->
      <div v-if="searchMode === 'steam'" class="steam-explanation">
        <p>
          <span class="icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width:20px;height:20px"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" /></svg>
          </span>
          <strong>Search Your Backlog:</strong> This mode finds games <em>within your own Steam library</em>. 
          It will NOT recommend new games to buy, but helps you decide what to play next from what you already own.
        </p>
      </div>

      <!-- Steam Profile Input (only when in Steam mode and no profile loaded) -->
      <div v-if="searchMode === 'steam' && !steamProfile" class="steam-input-section">

        <!-- Show input field only if user is NOT logged in OR is logged in but has NO linked Steam account -->
        <div v-if="!isLoggedIn || (isLoggedIn && !hasSteamLinked)" class="input-group">
          <div class="label-row">
            <label for="steam-profile">Steam Profile URL or ID</label>
            <button class="help-icon" @click="showSteamHelp = !showSteamHelp" title="How to find your Steam ID">
              ?
            </button>
          </div>
          
          <div v-if="showSteamHelp" class="help-box">
             <p><strong>To find your Steam ID:</strong></p>
             <ol>
               <li>Open your Steam Profile in a browser.</li>
               <li>Look at the URL bar. It should look like: <br><code>steamcommunity.com/profiles/7656...</code></li>
               <li>Copy the <strong>17-digit number</strong> (starting with 7656).</li>
               <li>If you see a custom name (e.g. <code>/id/username</code>), use a tool like <a href="https://steamid.io" target="_blank">steamid.io</a> to convert it to a 64-bit ID.</li>
             </ol>
          </div>

          <input
            id="steam-profile"
            v-model="steamProfileInput"
            type="text"
            placeholder="e.g. 76561198000000000"
            @keyup.enter="handleSteamProfileSubmit"
          />
      <button @click="handleSteamProfileSubmit" class="submit-btn" :disabled="steamProfileLoading">
        {{ steamProfileLoading ? 'Loading...' : (isLoggedIn ? 'Link Profile' : 'Load Profile') }}
      </button>
        </div>

        <!-- Show message if user is logged in but already has a linked Steam account -->
        <div v-if="isLoggedIn && hasSteamLinked" class="already-linked-message">
          <div class="info-icon">
            <!-- Info Icon -->
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:24px;height:24px"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          </div>
          <div>
            <p class="info-title">Steam Account Already Linked</p>
            <p class="info-description">You already have a Steam account linked. It will load automatically.</p>
          </div>
        </div>

        <div v-if="steamProfileLoading" class="loading-indicator">
          <div class="loading-spinner small"></div>
          <span>Loading profile...</span>
        </div>
        <div v-if="steamProfileError" class="error-message steam-error">
          <span class="error-icon">
            <!-- AlertTriangle Icon -->
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:24px;height:24px"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </span>
          <p>{{ steamProfileError }}</p>
        </div>
      </div>

      <!-- Steam Profile Section (only when profile loaded) -->
      <div v-if="searchMode === 'steam' && steamProfile" class="steam-profile-section">
        <div class="steam-profile-header">
          <img :src="steamAvatar" :alt="steamName" class="steam-avatar" />
          <div class="steam-profile-info">
            <h3>{{ steamName }}</h3>
            <p>
              Steam Profile
              <span v-if="hasSteamLinked" class="linked-text">• Linked to your account</span>
            </p>
          </div>
        </div>
        <button @click="clearSteamProfile" class="clear-profile-btn">
          {{ hasSteamLinked ? 'Unlink' : 'Clear Profile' }}
        </button>
      </div>

      <SearchBar ref="searchBar" @search="handleSearch" />


      <!-- Results Section -->
      <div v-if="warningMessage && hasSearched && !isLoading" :class="['results-warning', warningLevel || 'info']">
        <strong>{{ fallbackApplied ? 'Fallback active:' : 'Notice:' }}</strong>
        <span>{{ warningMessage }}</span>
      </div>

      <!-- Results Section -->
      <div v-if="games.length" class="results-section">
        <div class="results-header">
          <h2>Found {{ games.length }}{{ hasMore ? '+' : '' }} {{ games.length === 1 ? 'match' : 'matches' }}</h2>
          <p class="results-subtitle">Ranked by relevance to your search</p>
        </div>
        <GameList :games="games" />
      </div>

      <!-- Empty State -->
      <div v-else-if="!isLoading && hasSearched" class="no-results">
        <div class="no-results-icon">
             <!-- Search Icon -->
             <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:64px;height:64px;opacity:0.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        </div>
        <h3>No games found</h3>
        <p>Try broadening the query or removing one constraint.</p>
        <div class="no-results-tips">
          <span class="tip-chip">Try a broader genre</span>
          <span class="tip-chip">Remove one exclusion</span>
          <span class="tip-chip">Use fewer descriptive words</span>
        </div>
      </div>

      <!-- Loading State -->
      <div v-else-if="isLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Analyzing your preferences and finding perfect games...</p>
      </div>

      <!-- Error State -->
      <div v-if="errorMessage" class="error-message">
        <span class="error-icon">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width:24px;height:24px"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
        </span>
        <p>{{ errorMessage }}</p>
      </div>

      <!-- Infinite Scroll Trigger -->
      <div v-if="canLoadMore" ref="scrollTrigger" class="scroll-trigger"></div>

      <div v-if="canLoadMore && !isLoadingMore" class="load-more-fallback">
        <button class="load-more-btn" @click="loadMoreGames">
          Load more results
        </button>
      </div>

      <!-- Loading More State -->
      <div v-if="isLoadingMore" class="loading-more">
        <div class="loading-spinner small"></div>
        <span>Loading more games...</span>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
import SearchBar from './SearchBar.vue';
import Sidebar from './Sidebar.vue';
import GameList from './GameList.vue';
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

export default {
  name: 'HomePage',
  components: {
    SearchBar,
    Sidebar,
    GameList
  },
  data() {
    return {
      hasSearched: false,
      observer: null,
      searchMode: 'general',
      steamProfileInput: '',
      steamProfileLoading: false,
      steamProfileError: '',
      showSteamHelp: false,
      includeAnime: JSON.parse(localStorage.getItem('pref_include_anime') || 'false'),
      includeAdult: JSON.parse(localStorage.getItem('pref_include_adult') || 'false'),
      includeVr: JSON.parse(localStorage.getItem('pref_include_vr') || 'false'),
      randomize: false
    };
  },
  computed: {
    ...mapState(['games', 'isLoading', 'errorMessage', 'user', 'steamProfile', 'hasMore', 'isLoadingMore', 'warningMessage', 'warningLevel', 'fallbackApplied', 'relevantCount']),
    ...mapGetters(['isLoggedIn', 'canLoadMore']),
    isStoreInitialized() {
      return this.$store.state.isInitialized;
    },
    steamAvatar() {
      return this.steamProfile?.avatarfull || this.steamProfile?.avatar || '';
    },
    steamName() {
      return this.steamProfile?.personaname || this.steamProfile?.username || 'Steam User';
    },
    hasSteamLinked() {
      return !!this.user?.steam_id;
    },
    formattedGameCount() {
        // Format with comma (e.g. 50,000)
        return (this.$store.state.totalGameCount || 50000).toLocaleString();
    }
  },
  methods: {
    ...mapActions(['loadMoreGames', 'searchGames', 'fetchSteamProfile', 'unlinkSteamProfile']),
    ...mapMutations(['setSteamProfile', 'setSteamName', 'setSteamAvatar', 'clearSteamProfileData']),

    // Updated via Sidebar now
    updateFilter(key, value) {
        // Legacy or if needed to drive sidebar?
        // Sidebar drives Home, not vice versa usually for filters
        this[key] = value;
        if (this.hasSearched) {
            this.handleSearch({ query: this.$store.state.userInput });
        }
    },

    async setSearchMode(mode) {
      this.searchMode = mode;
      if (this.hasSearched) {
        this.$store.commit('setGames', []);
        this.hasSearched = false;
      }
      if (mode === 'general') {
        this.steamProfileError = '';
      } else if (mode === 'steam') {
        if (this.isLoggedIn && this.hasSteamLinked && !this.steamProfile) {
          this.steamProfileLoading = true;
          this.steamProfileError = '';
          try {
            await this.fetchSteamProfile(this.user.steam_id);
          } catch (error) {
            this.steamProfileError = 'Failed to load your linked Steam profile';
          } finally {
            this.steamProfileLoading = false;
          }
        }
      }
    },

    async handleSteamProfileSubmit() {
      if (!this.steamProfileInput.trim()) {
        this.steamProfileError = 'Please enter a Steam profile URL or ID';
        return;
      }

      this.steamProfileLoading = true;
      this.steamProfileError = '';

      try {
        const steamId = this.extractSteamId(this.steamProfileInput);
        await this.fetchSteamProfile(steamId);

        if (this.isLoggedIn) {
          this.$notify({
            title: 'Success',
            text: 'Steam profile linked to your account!',
            type: 'success',
          });
        }

        this.steamProfileInput = '';
      } catch (error) {
        this.steamProfileError = error.message || 'Failed to load Steam profile';
      } finally {
        this.steamProfileLoading = false;
      }
    },

    extractSteamId(input) {
      input = input.trim();

      const urlPattern = /(?:steamcommunity\.com\/id\/|steamcommunity\.com\/profiles\/)([^/\s]+)/;
      const match = input.match(urlPattern);

      if (match) {
        return match[1];
      }

      if (/^\d{17}$/.test(input)) {
        return input;
      }

      const fullUrlPattern = /(?:profiles\/)(\d+)/;
      const fullMatch = input.match(fullUrlPattern);
      if (fullMatch) {
        return fullMatch[1];
      }

      return input;
    },

    handleFilterUpdate(filters) {
        this.includeAnime = filters.includeAnime;
        this.includeAdult = filters.includeAdult;
        this.includeVr = filters.includeVr;
        this.randomize = filters.random;

        if (this.hasSearched && !this.isLoading) {
             const currentQuery = this.$store.state.userInput;
             if (currentQuery) {
                 this.handleSearch({ query: currentQuery });
             }
        }
    },

    handleSearch() {
      this.hasSearched = true;
      
      const searchOptions = {
          useSteam: this.searchMode === 'steam',
          includeAnime: this.includeAnime,
          includeAdult: this.includeAdult,
          includeVr: this.includeVr,
          random: this.randomize
      };

      if (this.searchMode === 'steam') {
        if (!this.steamProfile) {
          this.steamProfileError = 'Please load a Steam profile first';
          return;
        }
        this.$store.dispatch('searchGames', searchOptions);
      } else {
        this.$store.dispatch('searchGames', searchOptions);
      }
    },

    async clearSteamProfile() {
      if (this.isLoggedIn && this.hasSteamLinked) {
        try {
          await this.unlinkSteamProfile();
        } catch (error) {
          console.error('Failed to unlink Steam profile:', error);
          this.steamProfileError = 'Failed to unlink Steam profile from your account';
          return;
        }
      }

      this.steamProfileInput = '';
      this.steamProfileError = '';
      this.clearSteamProfileData();

      if (this.hasSearched) {
        this.$store.commit('setGames', []);
        this.hasSearched = false;
      }
    },

    setupIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }

      const options = {
        root: null, // Use the viewport
        rootMargin: '200px',
        threshold: 0.1,
      };

      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && this.canLoadMore) {
          this.loadMoreGames();
        }
      }, options);

      this.$nextTick(() => {
        if (this.$refs.scrollTrigger) {
          this.observer.observe(this.$refs.scrollTrigger);
        }
      });
    },
    maybeAutoloadMore() {
      if (!this.canLoadMore || this.isLoadingMore || this.isLoading) {
        return;
      }
      this.$nextTick(() => {
        const pageHeight = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight
        );
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        if (pageHeight <= viewportHeight + 240) {
          this.loadMoreGames();
        }
      });
    },
  },
  watch: {
    games(newGames) {
      if (newGames.length > 0 || this.errorMessage) {
        this.hasSearched = true;
      }
      if (newGames.length > 0) {
        this.maybeAutoloadMore();
      }
    },
    canLoadMore() {
      this.setupIntersectionObserver();
      this.maybeAutoloadMore();
    },
    isLoggedIn(newVal, oldVal) {
      if (newVal && !oldVal && this.searchMode === 'steam') {
        if (this.hasSteamLinked && !this.steamProfile) {
          this.fetchSteamProfile(this.user.steam_id).catch(err => {
            console.error('Failed to auto-load Steam profile:', err);
          });
        }
      }
    }
  },
  mounted() {
    this.setupIntersectionObserver();
  },
  updated() {
    this.setupIntersectionObserver();
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>

<style scoped>
#home {
  height: 100%;
  width: 100%;
  position: relative;
}

.app-layout {
  min-height: 100vh;
  /* Sidebar is fixed, so main content needs margin. */
}


.main-content {
  /* padding: 60px 24px 24px 24px; REMOVED old padding */
  /* Sidebar is 250px fixed. Content needs to start after that. */
  /* We use padding-left on the container to reserve space. */
  padding: 60px 24px 24px 304px; /* 280px sidebar + 24px content padding */
  width: 100%;
  max-width: 100%; /* Ensure it uses full width of parent */
  margin-left: 0; /* No margin needed */
  box-sizing: border-box; 
}

@media (max-width: 1024px) {
    .app-layout {
        flex-direction: column;
    }
    .main-content {
        /* Mobile: Sidebar is top/relative. Reset padding. */
        padding: 16px; 
        margin-left: 0;
        width: 100%;
    }
}

.initialization-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #0a0a0f;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.initialization-overlay p {
  color: #a78bfa;
  font-size: 1.1rem;
  margin-top: 20px;
  animation: pulse-text 1.5s ease-in-out infinite;
}

@keyframes pulse-text {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* Header styles moved to bottom */

.label-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.help-icon {
  background: rgba(167, 139, 250, 0.2);
  color: #a78bfa;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.help-icon:hover {
  background: rgba(167, 139, 250, 0.4);
}

.help-box {
  background: rgba(30, 30, 45, 0.8);
  border: 1px solid rgba(167, 139, 250, 0.2);
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 0.9rem;
  color: #ccc;
}

.help-box p {
  margin-bottom: 8px;
  color: #fff;
}

.help-box ol {
  padding-left: 20px;
  margin: 0;
}

.help-box li {
  margin-bottom: 4px;
}

.help-box a {
  color: #a78bfa;
}

.steam-explanation {
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.steam-explanation p {
  margin: 0;
  color: #d1d5db;
  font-size: 0.95rem;
  line-height: 1.5;
}

.steam-explanation strong {
  color: #10b981;
}

.steam-explanation .icon {
  margin-right: 8px;
}

.results-warning {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 12px 14px;
  margin-bottom: 20px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #d4d4d8;
  font-size: 0.95rem;
  line-height: 1.45;
}

.results-warning strong {
  color: #f4f4f5;
}

.results-warning.warning {
  border-color: rgba(245, 158, 11, 0.28);
  background: rgba(120, 53, 15, 0.16);
}

.results-warning.info {
  border-color: rgba(59, 130, 246, 0.24);
  background: rgba(30, 64, 175, 0.12);
}

.load-more-fallback {
  display: flex;
  justify-content: center;
  margin: 8px 0 16px;
}

.load-more-btn {
  padding: 12px 18px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  color: #f4f4f5;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.load-more-btn:hover {
  background: rgba(255, 255, 255, 0.09);
  border-color: rgba(255, 255, 255, 0.22);
  transform: translateY(-1px);
}

.no-results-tips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 18px;
}

.tip-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #d4d4d8;
  font-size: 0.88rem;
  font-weight: 600;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 20px;
  justify-content: center;
  margin-bottom: 20px;
}

.logo-icon-svg {
  width: 60px;
  height: 60px;
  color: #00dc82;
  filter: drop-shadow(0 0 15px rgba(0, 220, 130, 0.5));
}

.app-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 3.5rem;
  font-weight: 800;
  margin: 0;
  line-height: 1;
  letter-spacing: -1px;
  color: #00dc82;
  text-shadow: 0 0 15px rgba(0, 220, 130, 0.5);
  text-transform: uppercase;
}
</style>
